'use client';

import React from 'react';
import { useRouter } from 'next/navigation';
import styles from './home.module.css';

const ADMIN = process.env.NEXT_PUBLIC_ADMIN_API_BASE;

export default function HomePage() {
  const router = useRouter();
  const [loading, setLoading] = React.useState(true);
  const [user, setUser] = React.useState<any>(null);
  const [role, setRole] = React.useState<string>('guest');
  const [room, setRoom] = React.useState('');
  const [scheduled, setScheduled] = React.useState<any[]>([]);
  const [error, setError] = React.useState<string | null>(null);
  const [pwOpen, setPwOpen] = React.useState(false);
  const [pwNext, setPwNext] = React.useState('');
  const [pwConfirm, setPwConfirm] = React.useState('');
  const [pwMsg, setPwMsg] = React.useState<string|null>(null);

  React.useEffect(() => {
    const init = async () => {
      try {
        if (!ADMIN) {
          setLoading(false);
          return;
        }
        const me = await fetch(`${ADMIN}/api/me`, { credentials: 'include', mode: 'cors' });
        const j = await me.json().catch(() => ({}));
        if (!j?.user) {
          // redirect to /login retaining current path
          const rt = `${window.location.pathname}${window.location.search}${window.location.hash}`;
          router.push(`/login?returnTo=${encodeURIComponent(rt)}`);
          return;
        }
        setUser(j.user);
        setRole(j.user.role || 'user');
        // If admin/moderator, load scheduled list
        if (j.user.role === 'admin' || j.user.role === 'moderator') {
          try {
            const resp = await fetch(`${ADMIN}/admin/api/rooms/scheduled`, { credentials: 'include', mode: 'cors' });
            if (resp.ok) {
              const data = await resp.json();
              setScheduled(Array.isArray(data?.scheduled) ? data.scheduled.slice(0, 20) : []);
            }
          } catch {}
        }
      } catch (e) {
        // fallback: no redirect loop
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [router]);

  async function handleEnter(e?: React.FormEvent) {
    e?.preventDefault();
    setError(null);
    const name = (room || '').trim();
    if (!name) return;

    // Try to validate existence without creating side effects
    // For non-admin users, skip server existence checks to avoid CORS/permission issues
    if (!(role === 'admin' || role === 'moderator')) {
      router.push(`/rooms/${encodeURIComponent(name)}`);
      return;
    }

    let exists = false;
    try {
      if (ADMIN) {
        const s = await fetch(`${ADMIN}/admin/api/rooms/scheduled`, { credentials: 'include', mode: 'cors' });
        if (s.ok) {
          const dj = await s.json();
          const arr = Array.isArray(dj?.scheduled) ? dj.scheduled : [];
          exists = arr.some((r: any) => (r.room_name || '').toLowerCase() === name.toLowerCase());
        }
        if (!exists) {
          const a = await fetch(`${ADMIN}/admin/api/rooms`, { credentials: 'include', mode: 'cors' });
          if (a.ok) {
            const aj = await a.json();
            const list = Array.isArray(aj?.rooms) ? aj.rooms : [];
            exists = list.some((r: any) => (r.name || r.room || '').toLowerCase() === name.toLowerCase());
          }
        }
      }
    } catch {}

    if (!exists) {
      setError('Sala não encontrada. Verifique o nome e tente novamente.');
      return;
    }
    router.push(`/rooms/${encodeURIComponent(name)}`);
  }

  if (loading) return <main data-lk-theme="default" style={{ padding: 24 }}>Carregando…</main>;

  return (
    <div data-lk-theme="default" className={styles.container}>
      <main className={styles.main}>
      <div className={styles.header}>
        <h2 className={styles.title}>Bem-vindo{user ? `, ${user.name ?? ''}` : ''}</h2>
        <p className={styles.subtitle}>Digite o nome da sala para entrar.</p>
      </div>

      <div className={styles.grid}>
        {user && (
          <aside className={styles.card}>
            <div className={styles.cardHeader}>Minha Conta</div>
            <nav className={styles.actions}>
              <button className="lk-button" onClick={()=>setPwOpen(v=>!v)}>
                <span aria-hidden>🔒</span> Alterar senha
              </button>
              <button className="lk-button lk-button-destructive" onClick={async ()=>{ try{ await fetch(`${ADMIN}/api/logout`, { method:'POST', credentials:'include', mode:'cors' }); }catch{} router.push('/login'); }}>
                <span aria-hidden>🚪</span> Sair
              </button>
            </nav>
            {pwOpen && (
              <form onSubmit={async (e)=>{ e.preventDefault(); setPwMsg(null); if (pwNext !== pwConfirm) { setPwMsg('Senhas não conferem'); return; } const resp = await fetch(`${ADMIN}/api/me/password`, { method:'POST', credentials:'include', mode:'cors', headers:{'Content-Type':'application/json'}, body: JSON.stringify({ new_password: pwNext }) }); if(resp.ok){ setPwMsg('Senha atualizada'); setPwNext(''); setPwConfirm(''); setPwOpen(false); } else { try{ const j=await resp.json(); setPwMsg(j?.error || `Falha (${resp.status})`);}catch{ setPwMsg('Falha ao salvar'); } } }} style={{ marginTop:12, display:'grid', gap:8 }}>
                <input type="password" className="lk-form-control" placeholder="Nova senha" value={pwNext} onChange={e=>setPwNext(e.target.value)} required />
                <input type="password" className="lk-form-control" placeholder="Confirmar nova senha" value={pwConfirm} onChange={e=>setPwConfirm(e.target.value)} required />
                <button className="lk-button lk-button-primary" type="submit">Salvar</button>
                {pwMsg && <div style={{ color:'#fca5a5' }}>{pwMsg}</div>}
              </form>
            )}
          </aside>
        )}

        <section>
          <div className={styles.card}>
          <form onSubmit={handleEnter} className={styles.formRow}>
            <input
              value={room}
              onChange={(e) => setRoom(e.target.value)}
              placeholder="Nome da sala"
              className="lk-form-control"
              style={{ minWidth: 260 }}
            />
            <button className="lk-button lk-button-primary" type="submit">Entrar</button>
          </form>
          {error && (
            <div style={{ marginTop: 10, padding: 8, borderRadius: 6, background: 'var(--lk-danger, #ef4444)', color: '#fff' }}>{error}</div>
          )}
          </div>

          {(role === 'admin' || role === 'moderator') && (
            <section style={{ marginTop: 24 }}>
              <h3 style={{ marginBottom: 8 }}>Salas agendadas</h3>
              {scheduled.length === 0 ? (
                <div style={{ opacity: .7 }}>Nenhuma sala agendada.</div>
              ) : (
                <div className={styles.list}>
                  {scheduled.map((r) => (
                    <div key={r.id} className={styles.item}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{r.display_name || r.room_name}</div>
                        <div className={styles.meta}>{r.room_name}</div>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        {r.live_active ? <span style={{ fontSize: 12, color: 'var(--lk-success,#22c55e)' }}>Ao vivo</span> : <span className={styles.meta}>Offline</span>}
                        <button className="lk-button" onClick={() => router.push(`/rooms/${encodeURIComponent(r.room_name)}`)}>Entrar</button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          )}
        </section>
      </div>

      <div className={styles.footer}>Hospedado e Desenvolvido por <a href="https://sncresolve.com.br" rel="noopener">SNC Resolve</a>.</div>
      </main>
    </div>
  );
}
